import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query"; // Add useQueryClient
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { FileUpload } from "@/components/FileUpload";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Icons
import { ArrowLeft, Save, Trash2, CirclePlus } from "lucide-react";

import { postCash } from "@/services/finance";
import type { CreateCashRequest } from "@/types/finance";
// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/cash-flow-direct-method/add/"
)({
  component: AddCashFlowDirect,
});

function AddCashFlowDirect() {
  const navigate = useNavigate();
  const { company_financial_report } = useAccessPermission();
  const queryClient = useQueryClient(); // Add this line
  const companyId = localStorage.getItem("companyId");
  const [year, setYear] = React.useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    return storedYear ? Number(storedYear) : undefined;
  });

  const [docFile, setDocFile] = React.useState("");
  const { toast } = useToast();

  // Initialize state with predefined details
  const [cashDetails, setCashDetails] = React.useState<
    Array<{
      cashDetailType:
      | "OPERATING_A"
      | "INVESTING_B"
      | "FINANCING_C"
      | "CASH_YEARLY";
      cashDataType: "EXPENSE" | "INCOME";
      name: string;
      amount: number | string;
      remark: string;
      amountForBackend?: number;
    }>
  >([
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ລາຍຮັບຈາກກິດຈະການປົກກະຕິ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ປັບປຸງ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ຄ່າຫຼູ້ຍຫ້ຽນ, ຄ່າສູນເສຍມູນຄ່າ ແລະ ເງິນແຮ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ສ່ວນປ່ຽນແປງ ອາກອນເຢື້ອນຊຳລະ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ສ່ວນປ່ຽນແປງ ບັນຊີລູກຄ້າ ແລະ ໜີ້ຕ້ອງຮັບອື່ນໆ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "OPERATING_A",
      cashDataType: "EXPENSE",
      name: "ສ່ວນປ່ຽນແປງ ບັນຊີຜູ້ສະໜອງ ແລະ ໜີ້ຕ້ອງສົ່ງອື່ນໆ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "INVESTING_B",
      cashDataType: "EXPENSE",
      name: "ຈ່າຍເງິນອອກ ຊື້ຊັບສົມບັດຄົງທີ່",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "INVESTING_B",
      cashDataType: "EXPENSE",
      name: "ຮັບເງິນເຂົ້າ ຈາກການຂາຍຊັບສິນຄົງທີ່",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "INVESTING_B",
      cashDataType: "EXPENSE",
      name: "ຜົນກະທົບຂອງສ່ວນປ່ຽນແປງ ຂອບຂອງ ການລວມກິດຈະການ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "FINANCING_C",
      cashDataType: "EXPENSE",
      name: "ເງິນປັນຜົນຈ່າຍໃຫ້ຜູ້ຖືຮຸ້ນ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "FINANCING_C",
      cashDataType: "EXPENSE",
      name: "ເພີ່ມທຶນຂຶ້ນ ເປັນເງິນຮັບເຂົ້າຄັງ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "FINANCING_C",
      cashDataType: "EXPENSE",
      name: "ຮັບເງິນ ຈາກການຈໍາໜ່າຍພັນທະບັດ, ກູ້ຢືມ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "CASH_YEARLY",
      cashDataType: "INCOME",
      name: "ເງິນສົດລວມ ແລະ ສິ່ງທຽບເທົ່າເງິນສົດລວມ ໃນຕົ້ນປີ",
      amount: "",
      remark: "",
    },
    {
      cashDetailType: "CASH_YEARLY",
      cashDataType: "INCOME",
      name: "ເງິນສົດລວມ ແລະ ສິ່ງທຽບເທົ່າເງິນສົດລວມ ໃນທ້າຍປີ",
      amount: "",
      remark: "",
    },
  ]);

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.CASH_FLOW_DERIRECT_REPORT);

  const calculateTotal = React.useCallback(
    (type: string): number => {
      return cashDetails
        .filter((detail) => detail.cashDetailType === type)
        .reduce((sum, detail) => {
          // Use the numeric value from amountForBackend if available; otherwise, fallback to 0.
          const value = detail.amountForBackend
            ? Number(detail.amountForBackend)
            : 0;
          // Subtract expenses, add incomes.
          return detail.cashDataType === "EXPENSE" ? sum - value : sum + value;
        }, 0);
    },
    [cashDetails]
  );

  // For the CASH_YEARLY total, compute the difference between the last and the first entry.
  const totalCashYearly = React.useMemo(() => {
    const cashYearlyItems = cashDetails.filter(
      (detail) => detail.cashDetailType === "CASH_YEARLY"
    );
    if (cashYearlyItems.length < 2) return 0;

    // Retrieve the first and last items.
    const firstItem = cashYearlyItems[0];
    const lastItem = cashYearlyItems[cashYearlyItems.length - 1];

    // Get numeric amounts. Adjust this logic if you are not using amountForBackend.
    const firstAmount =
      firstItem.amountForBackend !== undefined
        ? Number(firstItem.amountForBackend)
        : Number(firstItem.amount) || 0;
    const lastAmount =
      lastItem.amountForBackend !== undefined
        ? Number(lastItem.amountForBackend)
        : Number(lastItem.amount) || 0;

    // The total is the last amount minus the first amount.
    return lastAmount - firstAmount;
  }, [cashDetails]);

  // Totals for the other types can be computed like so:
  const totalOperatingA = React.useMemo(
    () => calculateTotal("OPERATING_A"),
    [calculateTotal]
  );
  const totalInvestingB = React.useMemo(
    () => calculateTotal("INVESTING_B"),
    [calculateTotal]
  );
  const totalFinancingC = React.useMemo(
    () => calculateTotal("FINANCING_C"),
    [calculateTotal]
  );

  const getDetailsByType = (type: string) =>
    cashDetails.filter((detail) => detail.cashDetailType === type);

  const getSectionTitle = (type: string) => {
    const titles: Record<string, string> = {
      OPERATING_A: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      INVESTING_B: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການລົງທຶນ",
      FINANCING_C: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການປະກອບທຶນ",
      CASH_YEARLY: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  const mutation = useMutation({
    mutationFn: (data: CreateCashRequest) => postCash(data),
    onSuccess: () => {
      toast({
        title: "ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      localStorage.setItem("year", JSON.stringify(year));
      queryClient.invalidateQueries({
        queryKey: ["getCashDIRECT", companyId, year],
      }); // Fix this line
      navigate({ to: "/report/finance/company/cash-flow-direct-method" });
    },
    onError: (error: Error) => {
      console.log(error, "Error");

      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ບັນທຶກຂໍ້ມູນຫຼົ້ມເຫຼວ",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !year) return;

    if (!docFile) {
      toast({
        title: "ບັນທຶກລົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
        variant: "destructive",
      });
      return;
    }

    const modifiedFinancialDetails = cashDetails.map(
      ({ amountForBackend, ...rest }) => ({
        ...rest,
        amount: amountForBackend || 0, // Remove the bitwise OR operator (|) to preserve decimals
      })
    );

    // Check if 'amount' is invalid (NaN, null, or undefined) in any item
    const hasInvalidAmount = modifiedFinancialDetails.some(({ amount }) => {
      const numericAmount = Number(amount);
      return (
        Number.isNaN(numericAmount) || amount === null || amount === undefined
      );
    });

    if (hasInvalidAmount) {
      toast({
        title: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຖືກຕ້ອງ",
        description: "ກະລຸນາກວດສອບການປ້ອນຈຳນວນຕົວເລກໃນຟອມ",
        variant: "destructive",
      });
      return;
    }

    const data: CreateCashRequest = {
      companyId,
      yearly: year,
      docFile,
      cashType: "DIRECT",
      cashDetails: modifiedFinancialDetails,
    };

    console.log("Data", data);
    mutation.mutate(data);
  };

  const updateDetail = (
    type: string,
    typeIndex: number,
    field: string,
    value: string | number
  ) => {
    const newDetails = [...cashDetails];
    const actualIndex = cashDetails.findIndex(
      (detail) =>
        detail.cashDetailType === type &&
        cashDetails.filter((d) => d.cashDetailType === type).indexOf(detail) ===
        typeIndex
    );

    if (actualIndex !== -1) {
      if (field === "amount") {
        // Remove commas from input
        let processedValue = String(value).replace(/,/g, "");

        // Allow only valid characters (numbers, decimal point, parentheses)
        if (
          !/^[(]?\d*\.?\d*[)]?$/.test(processedValue) &&
          processedValue !== ""
        ) {
          return;
        }

        // Handle decimal places
        if (processedValue.includes(".")) {
          const [integerPart, decimalPart] = processedValue.split(".");
          if (decimalPart && decimalPart.replace(")", "").length > 2) {
            const limitedDecimal = decimalPart.replace(")", "").slice(0, 2);
            processedValue = `${integerPart}.${limitedDecimal}${processedValue.endsWith(")") ? ")" : ""
              }`;
          }
        }

        // Convert to number for backend
        const numericValue = processedValue.replace(/[()]/g, "");
        const numericAmount = Number(numericValue);

        newDetails[actualIndex] = {
          ...newDetails[actualIndex],
          amount: processedValue, // Store formatted value for display
          // amountForBackend: processedValue.startsWith('(') && processedValue.endsWith(')') ? -numericAmount : numericAmount,
          amountForBackend: numericAmount,
          cashDataType:
            processedValue.startsWith("(") && processedValue.endsWith(")")
              ? "EXPENSE"
              : "INCOME",
        };
      } else {
        newDetails[actualIndex] = {
          ...newDetails[actualIndex],
          [field]: value,
        };
      }
      setCashDetails(newDetails);
    }
  };

  const addNewRow = (
    type: "OPERATING_A" | "INVESTING_B" | "FINANCING_C" | "CASH_YEARLY"
  ) => {
    setCashDetails([
      ...cashDetails,
      {
        cashDetailType: type,
        cashDataType: "INCOME",
        name: "",
        amount: "",
        remark: "",
      },
    ]);
  };

  const removeRow = (type: string, index: number) => {
    const itemsOfType = getDetailsByType(type);
    const newDetails = cashDetails.filter(
      (item) => item !== itemsOfType[index]
    );
    setCashDetails(newDetails);
  };

  // NEw Function
  const formatNumberWithCommas = (value: string | number) => {
    if (!value && value !== 0) return "";

    // Convert to string and remove commas
    const stringValue = String(value).replace(/,/g, "");

    // Check if the value is wrapped in parentheses
    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

    // Split number into integer and decimal parts
    const [integerPart, decimalPart] = numericValue.split(".");

    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Combine with decimal part if it exists
    const formattedValue =
      decimalPart !== undefined
        ? `${formattedInteger}.${decimalPart}`
        : formattedInteger;

    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  // Add this formatter function near the top of your component

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: {
                  page: 1,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນກະແສເງິນສົດ (ວິທີທາງກົງ)</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Year Input */}
            {/* <div className="flex flex-col gap-2 mb-4">
							<Label>ປະຈຳປີ</Label>
							<div className="flex gap-x-2 items-center">
								<Input
									type="number"
									value={year}
									onChange={handleYearChange}
									placeholder="ປ້ອນປີ..."
									className="max-w-[300px]"
									min={1900}
								/>
								{yearError && (
									<span className="text-sm text-red-500">{yearError}</span>
								)}
							</div>
						</div> */}

            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={year ? String(year) : ""}
                onValueChange={(value) => {
                  setYear(Number(value));
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    {
                      length: new Date().getFullYear() - 1974,
                    },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <Table className="border">
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableHead>ເນື້ອໃນລາຍງານ</TableHead>
                  <TableHead>
                    ມູນຄ່າຍັງເຫຼືອໃນປີນີ້{" "}
                    <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>
                  <TableHead>ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/*TODO: ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ A */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>
                    {getSectionTitle("OPERATING_A")}
                  </TableCell>
                </TableRow>

                {getDetailsByType("OPERATING_A").map((detail, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={detail.name}
                        onChange={(e) =>
                          updateDetail(
                            "OPERATING_A",
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        readOnly={index < 6}
                      // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumberWithCommas(detail.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers, decimal point, parentheses, and commas
                          if (
                            /^[(]?\d*\.?\d*[)]?$/.test(
                              value.replace(/,/g, "")
                            ) ||
                            value === ""
                          ) {
                            updateDetail("OPERATING_A", index, "amount", value);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={detail.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) =>
                          updateDetail(
                            "OPERATING_A",
                            index,
                            "remark",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {index > 5 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRow("OPERATING_A", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                  size="icon"
                  onClick={() => addNewRow("OPERATING_A")}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>
                    ກະແສເງິນສົດລວມ ທີ່ຕິດພັນກັບການທຸລະກິດ (ກ)
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(totalOperatingA)}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>

                {/* TODO: ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ) */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>
                    {getSectionTitle("INVESTING_B")}
                  </TableCell>
                </TableRow>

                {getDetailsByType("INVESTING_B").map((detail, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={detail.name}
                        onChange={(e) =>
                          updateDetail(
                            "INVESTING_B",
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        readOnly={index < 3}
                      // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumberWithCommas(detail.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers, decimal point, parentheses, and commas
                          if (
                            /^[(]?\d*\.?\d*[)]?$/.test(
                              value.replace(/,/g, "")
                            ) ||
                            value === ""
                          ) {
                            updateDetail("INVESTING_B", index, "amount", value);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={detail.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) =>
                          updateDetail(
                            "INVESTING_B",
                            index,
                            "remark",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {index > 2 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRow("INVESTING_B", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                  size="icon"
                  onClick={() => addNewRow("INVESTING_B")}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ)</TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(totalInvestingB)}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>

                {/* TODO: ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ ( ຄ ) */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>
                    {getSectionTitle("FINANCING_C")}
                  </TableCell>
                </TableRow>

                {getDetailsByType("FINANCING_C").map((detail, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={detail.name}
                        onChange={(e) =>
                          updateDetail(
                            "FINANCING_C",
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        readOnly={index < 3}
                      // disabled={[0, 1, 2, 3, 4, 5].includes(index)}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumberWithCommas(detail.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers, decimal point, parentheses, and commas
                          if (
                            /^[(]?\d*\.?\d*[)]?$/.test(
                              value.replace(/,/g, "")
                            ) ||
                            value === ""
                          ) {
                            updateDetail("FINANCING_C", index, "amount", value);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={detail.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) =>
                          updateDetail(
                            "FINANCING_C",
                            index,
                            "remark",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {index > 2 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRow("FINANCING_C", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                  size="icon"
                  onClick={() => addNewRow("FINANCING_C")}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ (ຄ)</TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(totalFinancingC)}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>
                    ສ່ວນປ່ຽນແປງກະແສເງິນສົດລວມໃນປີ ( ກ + ຂ + ຄ )
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(
                      totalOperatingA + totalInvestingB + totalFinancingC
                    )}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>

                {/* TODO: CASH YEARLY */}
                {getDetailsByType("CASH_YEARLY").map((detail, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell>
                      <p>{detail.name}</p>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumberWithCommas(detail.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers, decimal point, parentheses, and commas
                          if (
                            /^[(]?\d*\.?\d*[)]?$/.test(
                              value.replace(/,/g, "")
                            ) ||
                            value === ""
                          ) {
                            updateDetail("CASH_YEARLY", index, "amount", value);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={detail.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) =>
                          updateDetail(
                            "CASH_YEARLY",
                            index,
                            "remark",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {index > 2 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRow("CASH_YEARLY", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell className="text-center">
                    ສ່ວນປ່ຽນແປງ ເງິນສົດລວມ
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(totalCashYearly)}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* File Input */}
            <div className="flex gap-x-1 items-center mt-4">
              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>

            {/* Submit Buttons */}
            {company_financial_report.isWrite && (
              <div className="flex gap-x-2 justify-end mt-4">
                <Button
                  type="button"
                  onClick={() =>
                    navigate({
                      to: "/report/finance",
                      search: {
                        page: 1,
                        startYear: 0,
                        endYear: 0,
                      },
                    })
                  }
                  variant={"outline"}
                >
                  ຍົກເລີກ
                </Button>
                <Button
                  disabled={mutation.isPending}
                  type="submit"
                  className="flex gap-x-2 items-center"
                >
                  {mutation.isPending ? (
                    <Spinner className="text-white" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      ບັນທຶກ
                    </>
                  )}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
